// Applicant Support component for the frontend
// Author: robwalls3
// Objectives:
//   1. Provide a dashboard of unhandled comms from applicants who have not yet submitted
//      - dashboard should be a simple list of applicants (Dynamo Dashboards)
//         - each applicant should have their Name, Email, and Phone Number with 
//           a link to their profile (see objective 2)
//   2. Provide a way to click into each of those applicants and Provide the info
//      - the info can be formatted in two ways:
//      - a) a list
//      - b) formatted like the survey
//      - a is easier so we will start there and expand if needed.
//   3. Provide a way to Communicate with the apps (Use Comms)
//   4. Provide a way to take Notes on the apps (i.e., I tried contacting, but they didn't respond. I'll try again later.)
//      - there should be a way to change the state of the app in such a way that it can be reflected on the dashboard
//         -- i.e. add Note "Contact Attempted: 7/27/2022, 12:05 PM MDT"
//         -- to do this we can display the notes in sequential order for each 'contact' on the dashboard?

import { FunnelIcon } from "@heroicons/react/24/outline";

import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { useHistory, useParams } from "react-router-dom";
import { usePost } from "./API";
import InterfaceContext, { ConfigurationContext, SupportedLanguage, UserInfoContext, PublicConfigurationContext } from "./Context";
import { langToWord, useLocalizedStrings } from "./Localization";
import { Dropdown } from "./Components/Dropdown";
import { snakeToEnglish, SpacedSpinner, useInterval } from "./Util";
import { SupportCaseBadge } from "./SupportCase";

import { caseStatuses, fields, getStageFromRequest, stages, SupportApplicantStage, TableKey, SupportRequest } from "./SupportUtil";
import { LanguageIcon } from "./Components/LanguageDropdown";
import { hash } from "@aidkitorg/types/lib/translation/v0_to_legacy";
import { Translations } from "@aidkitorg/i18n/lib";
import { Button, Icon } from "@aidkitorg/component-library";
import { ColumnDef } from "@tanstack/react-table";
import { SupportTableV2 } from "./SupportTableV2";
import { SupportTableV3 } from "./SupportTableV3";
import { RichText, Text } from "@aidkitorg/types/lib/survey";
import { GetOrCreateSupportCaseParams, SupportRow } from "./Support.types";
import { useModularMarkdown } from "./Hooks/ModularMarkdown";

const localizeStageFn = (L: Translations) => (stage: string) => {
  switch (stage) {
    case "No Application":
      return L.application_stage.pre_application;
    case "In Progress":
      return L.application_stage.in_progress;
    case "Applied":
      return L.application_stage.applied;
    case "All Stages":
      return L.support.all_stages;
    default:
      return stage;
  }
}

type FieldColumn = {
  kind: 'field';
  key: TableKey;
  sortable: boolean;
};

type ExtraColumn = {
  kind: 'extraColumn';
  key: string;
  label: string;
  filterable?: boolean;
};

type Column = FieldColumn | ExtraColumn;

function langForDate(lang: SupportedLanguage) {
  return lang.replaceAll('_', '-');
}

const makeSupportCaseUrl = (channel: string, supportCaseId: string) => `/supportcasev2/${channel || "default"}/${supportCaseId}`;

export function SupportPageInner(props: any) {
  const L = useLocalizedStrings();
  const localizeStage = localizeStageFn(L);
  const { customId, channel } = useParams() as any;
  const [mainFilter, setMainFilter] = useState(customId);
  const context = useContext(InterfaceContext);
  const config = useContext(ConfigurationContext);
  const publicConfig = useContext(PublicConfigurationContext);

  const [targetFieldFilterOptions, setTargetFieldFilterOptions] = useState({} as Record<string, string[]>);
  const [targetFieldFilters, setTargetFieldFilters] = useState({} as Record<string, string>);
  const [allColumns, setAllColumns] = useState<ColumnDef<SupportRequest['row']>[]>(() =>
    // Initialize with field headers for skeleton loader
    fields.map(field => ({ accessorKey: field.key, header: L.support.fields[field.key] }))
  );

  // load sortKey, sortOrder, filteredStage, and filteredLanguage from search params
  const params = new URLSearchParams(window.location.search);

  const fetchSupport = usePost("/applicants/support");

  const [supportRequests, setSupportRequests] = useState([] as SupportRequest[]);
  const [fetchTime, setFetchTime] = useState(0);
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [supportDashboard, setSupportDashboard] = useState<{ name: Text, description?: RichText } | null>(null);
  const [extraColumns, setExtraColumns] = useState<any>([]);

  const [sortKey, setSortKey] = useState<TableKey>(params.get("sortKey") as TableKey ?? "min");

  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(params.get("sortOrder") as "desc" ?? "asc");

  const [filteredStage, setFilteredStage] = useState<SupportApplicantStage | null>(params.get("filteredStage") as SupportApplicantStage ?? null);

  const acceptableRates = [30, 60, 180, 300, -1];
  const queryRate = parseInt(params.get("refreshRate") || '60');
  const [refreshRate, setRefreshRate] = useState(acceptableRates.includes(queryRate) ? queryRate : 60); // Refresh every 60s by default

  // Language filter
  const [filteredLanguage, setFilteredLanguage] = useState<SupportedLanguage | null>(params.get("filteredLanguage") as SupportedLanguage ?? null);
  const [requestLanguages, setRequestLanguages] = useState([] as (SupportedLanguage)[]);

  const [rows, setRows] = useState([] as (JSX.Element | null)[]);
  
  const None = 'No Filter';

  // Prevent horizontal scrolling of the entire page when a table extends beyond the viewport
  useEffect(() => {
    document.body.classList.add('overflow-x-hidden');
    return () => {
      document.body.classList.remove('overflow-x-hidden');
    };
  }, []);

  function sortRequests(requests: SupportRequest[], sortKey: TableKey, sortOrder: "asc" | "desc") {
    const sorted = requests.sort((a, b) => {
      let neg = sortOrder === 'desc' ? 1 : -1;
      let pos = sortOrder === 'desc' ? -1 : 1;
      if (!a || !b) {
        return 0;
      }

      if (sortKey === 'unhandled_messages') {
        return (a.row.messages || []).length < (b.row.messages || []).length ? neg : pos;
      }

      if (sortKey === "last_message_text") {
        // Should never get here since the column is not sortable, but return 0 to make TS happy
        return 0;
      }

      if (sortKey === 'applicant_stage') {
        return getStageFromRequest(a) < getStageFromRequest(b) ? neg : pos;
      } else {
        if (!a.row[sortKey]) return neg;
        if (!b.row[sortKey]) return pos;
        return a.row[sortKey]! < b.row[sortKey]! ? neg : pos;
      }
    });
    return sorted;
  }

  const handleV2SortAndFilter = useCallback((key: TableKey, switchOrder: boolean, dataSet?: SupportRequest[]) => {
    const newSortOrder = switchOrder ? (sortOrder === 'asc' ? 'desc' : 'asc') : sortOrder;
    const toSort = dataSet || supportRequests;
    const sorted = sortRequests([...toSort], key, newSortOrder);
    
    setSortOrder(newSortOrder);
    setSortKey(key);

    // Create JSX rows for V2
    const jsxRows = [...sorted].map((request, idx: number) => {
      const app = request.row.source;
      if (!app) return null;
      if (filteredStage && getStageFromRequest(request) !== filteredStage) return null;
      if (filteredLanguage && request.row.language !== filteredLanguage) return null;
      for (const [field, filterValue] of Object.entries(targetFieldFilters)) {
        if (filterValue) {
          let matchFound = false;
          for (const applicantInfo of Object.values(request.applicantInfos)) {
            if (applicantInfo[field] === filterValue) {
              matchFound = true;
              break;
            }
          }
          if (!matchFound) return null;
        }
      }

      return <tr id={"approw-" + app} key={hash(JSON.stringify(request.row))} className="border border-b-4 border-gray-600">
        <MappedSupportRequest idx={idx} request={request} app={app} allColumns={allColumns.map(col => {
          const accessorKey = (col as any).accessorKey as string;
          const field = fields.find(f => f.key === accessorKey);
          return field ? {
            kind: 'field' as const,
            key: field.key as string,
            sortable: field.sortable
          } : {
            kind: 'extraColumn' as const,
            key: accessorKey,
            label: (col as any).header as string,
            filterable: true
          };
        })} channel={channel} />
      </tr>
    }).filter(Boolean);

    setRows(jsxRows);
  }, [filteredStage, filteredLanguage, supportRequests, targetFieldFilters, allColumns, sortOrder]);

  const cb = useCallback(() => {
    (async () => {
      setLoading(true);
      const dashboard = await fetchSupport({
        channel: channel || "default",
        ...(mainFilter === 'mine' ? { mine: true } : {}),
        ...(mainFilter && caseStatuses.indexOf(mainFilter as typeof caseStatuses[number]) !== -1 ?
          { status: mainFilter } : {})
      });
      setLoading(false);
      if (!dashboard || (dashboard as any).error) return;

      if (!loadedOnce) setLoadedOnce(true);

      setFetchTime(dashboard.fetchTime!);

      if (!dashboard.supportRequests) {
        console.log("No support requests found");
        return;
      }

      setSupportRequests(dashboard.supportRequests);
      handleV2SortAndFilter(sortKey, false, dashboard.supportRequests);
      setSupportDashboard({ name: dashboard.name, description: dashboard.description });
      setExtraColumns(dashboard.targetFieldConfigs || []);
      // Set unique languages 
      const languages = new Set(dashboard.supportRequests.map(r => r.row.language).filter(l => l));
      setRequestLanguages(Array.from(languages) as SupportedLanguage[]);
      setTargetFieldFilterOptions(dashboard.targetFieldFilterOptions);
      setAllColumns(() => {
        const columns: ColumnDef<SupportRequest['row']>[] = [];
        fields.forEach((field) => {
          // Truncate source to prevent long email addresses from overflowing into the next columns
          if (field.key === 'source') {
            columns.push({
              accessorKey: field.key,
              header: L.support.fields[field.key],
              size: 200, // Make source column a bit wider for better email address visibility
              cell: ({ row }) => (
                <div className="truncate" title={row.original.source || undefined}>
                  {row.original.source}
                </div>
              ),
            });
          } else if (field.key === 'last_message_text') {
            columns.push({
              accessorKey: field.key,
              header: L.support.fields[field.key],
              size: 250,
              cell: ({ row }) => {
                const messages = row.original.messages;
                if (!messages || messages.length === 0) return null;
                // Messages are ordered by created_at DESC from backend (most recent first)
                const lastMessage = messages[0];
                return (
                  // Limit the preview to 3 lines max and truncate with an ellipsis if needed
                  <div className="whitespace-pre-wrap break-words overflow-hidden line-clamp-3" title={lastMessage}>
                    {lastMessage}
                  </div>
                );
              }
            });
          } else {
            columns.push({
              accessorKey: field.key,
              header: L.support.fields[field.key],
            });
          }
        });
        for (const c of dashboard.targetFieldConfigs) {
          // insert the column at the index specified by the columnIndex or at the end if undefined or out of range
          columns.splice(c.columnIndex ?? columns.length, 0, { accessorKey: c.targetField, header: snakeToEnglish(c.targetField) });
        }
        return columns;
      });
    })();
  }, [mainFilter, channel]);

  const updateURL = (options: {
    path?: 'mine' | 'all_unhandled' | string,
    params?: Record<string, string | number | null>
  }) => {
    let { params } = options;
    let newPath = new URLSearchParams(window.location.search);
    for (const param of ["filteredStage", "filteredLanguage", "sortKey", "sortOrder", "refreshRate"]) {
      // If params are not passed, we will just keep the existing ones.
      if (params) {
        if (params[param]) {
          newPath.set(param, params[param] + "");
        } else {
          newPath.delete(param);
        }
      }
    }
    window.history.replaceState({}, '', `${options.path || window.location.pathname}?${newPath}`);
  }

  const enableModernSupportTable = publicConfig?.experimental?.enableModernSupportTable;

  // This use effect sorts when we update stage or language
  // Because those are filtered in the handleSort method.
  useEffect(() => {
    if (!enableModernSupportTable) {
      handleV2SortAndFilter(sortKey, false);
    }
  }, [filteredStage, filteredLanguage, targetFieldFilters, allColumns, enableModernSupportTable]);

  // This use effect updates search params when any of the filters/sorts changes so that
  // The link can be copied and the exact same view can be shared.
  useEffect(() => {
    updateURL({ params: { filteredStage, filteredLanguage, sortKey, sortOrder, refreshRate } });
  }, [filteredStage, filteredLanguage, sortKey, sortOrder, refreshRate]);

  useInterval(() => { if (refreshRate > 0 && !document.hidden) cb() }, refreshRate * 1000);
  useEffect(() => {
    cb();
  }, [mainFilter, channel]);

  const localizeStatusOrFilter = (status: string) => {
    switch (status) {
      case "mine":
        return L.support.mine;
      case "all_unhandled":
        return L.support.all_unhandled;
      case "open":
        return L.support.open;
      case "escalated":
        return L.support.escalated;
      case "resolved":
        return L.support.resolved;
      case "viewed":
        return L.support.viewed;
      default:
        return snakeToEnglish(status);
    }
  }

  const createSupportCaseRequest = usePost("/support/create_case");
  const reassignSupportRequest = usePost("/support/reassign_case");
  const user = useContext(UserInfoContext);

  const getOrCreateSupportCase = useCallback(async (row: GetOrCreateSupportCaseParams, leaveUnassigned?: boolean): Promise<string> => {
    // we've already got a case, no need to create one.
    let supportCase = row.support_case;
    // Case 1: Support case already exists (that the frontend knows about)
    if (supportCase) {
      // Only attempt reassignment if:
      // 1. We're not leaving it unassigned (i.e. user is claiming the case)
      // 2. Current assignee is "system" (indicating unclaimed case)
      if (!leaveUnassigned && row.assigned_to?.toLowerCase() === 'system') {
        const result = await reassignSupportRequest({ caseId: supportCase, reassignTo: user.uid!, failIfAssigned: true });
        // If reassignment succeeds, update UI to show current user as assignee
        if (!('error' in result)) {
          setSupportRequests(cur => cur.map(request => {
            if (request.row.source === row.source) {
              return {
                ...request,
                row: {
                  ...request.row,
                  assigned_to: user.uid!,
                  assigned_to_name: config.user?.name || 'Me',
                  support_case_status: 'open'
                }
              }
            }
            return request;
          }));
        }
      }
    // Case 2: No known existing support case - create a new one
    } else {
      const result = await createSupportCaseRequest({
        leaveUnassigned,
        channel: channel || "default",
        ...(row.source ? { contact: row.source } : {})
      });
      if (result.supportCase) {
        supportCase = result.supportCase.uid;
        // Update local state with the new support case
        setSupportRequests(cur => {
          return cur.map(request => {
            if (request.row.source === row.source) {
              return {
                ...request,
                row: {
                  ...request.row,
                  support_case: supportCase,
                  support_case_status: leaveUnassigned ? 'viewed' : 'open',
                  assigned_to: leaveUnassigned ? 'system' : user.uid!,
                  assigned_to_name: leaveUnassigned ? 'system' : config.user?.name || 'Me',
                }
              }
            }
            return request;
          })
        })
      }

      if (!supportCase) {
        throw new Error('unable to create support case at this time');
      }
    }

    return supportCase;
  }, [channel, user.uid, config.user?.name, reassignSupportRequest, createSupportCaseRequest]);

  // Temporary until internal filters is implemented in SupportTableV3
  const filteredSupportRequests = useMemo(() => supportRequests.filter(request => {
    if (!request.row.source) {
      return false;
    }
    if (filteredStage && getStageFromRequest(request) !== filteredStage) {
      return false;
    }
    if (filteredLanguage && request.row.language !== filteredLanguage) {
      return false;
    }
    for (const [field, filterValue] of Object.entries(targetFieldFilters)) {
      if (filterValue) {
        let matchFound = false;
        for (const applicantInfo of Object.values(request.applicantInfos)) {
          if (applicantInfo[field] === filterValue) {
            matchFound = true;
            break;
          }
        }
        if (!matchFound) {
          return false;
        }
      }
    }
    return true;
  }), [supportRequests, filteredStage, filteredLanguage, targetFieldFilters]);

  const description = useModularMarkdown({ content: supportDashboard?.description?.[context.lang] || supportDashboard?.description?.en || '', info: {} });

  return <div className="py-8">
    <header>
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between">
          <div>
            <h1 className="text-3xl font-bold leading-tight text-gray-900 mb-3">{supportDashboard?.name?.[context.lang] || supportDashboard?.name?.en}</h1>
            <h2 className="text-sm font-medium text-gray-600 mb-3">
              {description}
            </h2>
            <h3 className="text-lg font-bold leading-tight text-gray-600">
              {mainFilter === 'mine'
                ? L.support.my_support_requests
                : mainFilter
                  ? L.support.filtered_support_requests.replace('{}', snakeToEnglish(mainFilter))
                  : L.support.all_support_requests}
            </h3>
            {(config.roles || '').indexOf('admin') !== -1 && <small title="Only visible to admin" className="text-gray-500 text-xs font-normal">{L.support.fetched_in_ms.replace('{}', fetchTime.toString())}</small>}
          </div>
          <div className="flex">
            {loading && loadedOnce && <SpacedSpinner />}
            <Dropdown label={<div className="p-0 flex items-center">
              <LanguageIcon />&nbsp;{(filteredLanguage ? langToWord(filteredLanguage!, "Name") : '')}
            </div>}
            description="Language"
            options={
              [null, ...requestLanguages].map(l => ({
                label: l ? langToWord(l, "Name") : None,
                callback: () => {
                  setFilteredLanguage(l)
                }
              }))
            } />&nbsp;
            <Dropdown usePopper={true} label={localizeStage(filteredStage || 'All Stages')} options={
              [null, ...stages].map(stage => ({
                label: localizeStage(stage || 'All Stages'),
                callback: () => {
                  setFilteredStage(stage);
                }
              }))
            } />&nbsp;
            <Dropdown usePopper={true} label={localizeStatusOrFilter(mainFilter)} options={
              ['mine', 'all_unhandled', ...caseStatuses].map(status => ({
                label: localizeStatusOrFilter(status),
                callback: () => {
                  // window.location.pathname = '/supportrequests/' + status;
                  setMainFilter(status);
                  updateURL({ path: status });
                }
              }))
            } />&nbsp;
            <Dropdown usePopper={true} label={refreshRate === -1 ? L.support.never : `${L.support.every_x_seconds.replace('{}', refreshRate.toString())}`} options={[
              { label: "30s", callback: () => setRefreshRate(30) },
              { label: L.stats.one_minute, callback: () => setRefreshRate(60) },
              { label: "3 " + L.stats.minutes, callback: () => setRefreshRate(180) },
              { label: "5 " + L.stats.minutes, callback: () => setRefreshRate(300) },
              { label: L.support.never, callback: () => setRefreshRate(-1) }
            ]} />
          </div>
        </div>
      </div>
    </header>
    <main className="px-4">
      <span>{loading && !loadedOnce && !enableModernSupportTable ? <>
        <SpacedSpinner /><span>{L.selfie.loading}</span>
      </> : null}</span>
      <div className="mt-8 flex flex-col max-w-full" style={{ maxHeight: '70vh' }}>
        {!loading && (!rows || rows.length === 0) && (
          <div className="text-center text-gray-500">{L.support.no_requests_found}</div>
        )}
        {enableModernSupportTable ? (
          <SupportTableV3
            filter={mainFilter}
            data={filteredSupportRequests}
            channel={channel}
            getOrCreateSupportCase={getOrCreateSupportCase}
            makeSupportCaseUrl={makeSupportCaseUrl}
            loading={loading && !loadedOnce}
            extraColumns={extraColumns}
          />
        ) : (
          rows && rows.length > 0 && (
            <SupportTableV2
              allColumns={allColumns}
              rows={rows}
              handleSort={handleV2SortAndFilter}
              sortKey={sortKey}
              sortOrder={sortOrder}
              targetFieldFilters={targetFieldFilters}
              setTargetFieldFilters={setTargetFieldFilters}
              targetFieldFilterOptions={targetFieldFilterOptions}
              L={L}
              channel={channel}
            />
          )
        )}
      </div>
    </main>
  </div>
}

export function MappedSupportRequest(props: {
  app: string,
  channel: string,
  request: SupportRequest,
  idx: number,
  allColumns: Array<{ kind: 'field', key: string, sortable: boolean } | { kind: 'extraColumn', key: string, label: string, filterable?: boolean }>
}) {
  const [app, setApp] = useState(props.app);
  const [request, setRequest] = useState(props.request);
  const config = useContext(ConfigurationContext);
  const context = useContext(InterfaceContext);
  const history = useHistory();
  const user = useContext(UserInfoContext);

  const L = useLocalizedStrings();
  const localizeStage = localizeStageFn(L);

  const createSupportCaseRequest = usePost("/support/create_case");
  const reassignSupportRequest = usePost("/support/reassign_case");

  async function getOrCreateSupportCase(leaveUnassigned?: boolean): Promise<string> {
    // we've already got a case, no need to create one.
    let supportCase = request.row.support_case;

    // Case 1: Support case already exists (that the frontend knows about)
    if (supportCase) {
      // Only attempt reassignment if:
      // 1. We're not leaving it unassigned (i.e. user is claiming the case)
      // 2. Current assignee is "system" (indicating unclaimed case)
      if (!leaveUnassigned && request.row.assigned_to?.toLowerCase() === 'system') {
        const result = await reassignSupportRequest({ caseId: supportCase, reassignTo: user.uid!, failIfAssigned: true });
        // If reassignment fails, update UI with current case info
        if ('error' in result) {
          if (result.caseInfo) {
            setRequest({
              ...request,
              row: {
                ...request.row,
                assigned_to: result.caseInfo.assigned_to,
                assigned_to_name: result.caseInfo.agent_name,
                support_case_status: result.caseInfo.status
              }
            });
          }
        // If reassignment succeeds, update UI to show current user as assignee
        } else {
          setRequest({
            ...request,
            row: {
              ...request.row,
              assigned_to: user.uid!,
              assigned_to_name: config.user?.name || 'Me',
              support_case_status: "open"
            }
          });
        }
      }

    // Case 2: No known existing support case - create a new one
    } else {
      const result = await createSupportCaseRequest({
        leaveUnassigned,
        channel: props.channel || "default",
        ...(request.row.source ? { contact: request.row.source } : {}),
        ...(Object.keys(request.applicantInfos).length > 0 ? { uid: Object.keys(request.applicantInfos)[0] } : {})
      });
      if (result.supportCase) {
        supportCase = result.supportCase.uid;
        // Update local state with the new support case
        setRequest({
          ...request,
          row: {
            ...request.row,
            ...result.supportCase,
            assigned_to: result.supportCase.assigned_to,
            assigned_to_name: (leaveUnassigned ? 'system' : config.user?.name || 'Me'),
            support_case: result.supportCase.uid,
            support_case_status: "open"
          }
        });
      }
    }

    if (!supportCase) {
      throw new Error('unable to create support case at this time');
    }

    return supportCase;
  }

  const makeSupportCaseUrl = (supportCaseId: string) => `/supportcasev2/${props.channel ?? "default"}/${supportCaseId}`;

  const getDisplayText = (row: SupportRequest['row'], fieldKey: string): string => {
    if (fieldKey === 'last_message_text') {
      // If it's a call and we have a transcript, show the transcript
      if (row.last_message_text === '[Call]' && row.last_call_transcript) {
        return `TRANSCRIPT: ${row.last_call_transcript}`;
      }
      // Otherwise show the original message
      return row.last_message_text || '';
    }
    
    // For other fields, just return the original value
    return (row as any)[fieldKey] || '';
  };

  const maskSystemUser = (row: any, field: { key: string }): any => {
    const value = getDisplayText(row, field.key);
    if (value?.toLowerCase?.() === 'system' && field.key === 'assigned_to_name') {
      return '';
    }
    return value;
  };

  return <>
    <td key={app + "-action-case"}>
      <ul className="flex gap-2 items-center ml-2 mt-3 whitespace-nowrap text-right text-sm font-medium sm:pr-6">
        <li>
          <Button 
            onClick={async () => {
              let caseId = await getOrCreateSupportCase(true);
              window.open(makeSupportCaseUrl(caseId!), '_blank');
            }}
            variant="secondary"
          >
            {L.support.view_case}
          </Button>
        </li>
        <li hidden={!!request.row.support_case && request.row.assigned_to?.toLowerCase() !== 'system'}>
          <Button
            onClick={async () => await getOrCreateSupportCase(false)}
            variant="primary"
          >
            {L.dashboard.claim}
          </Button>
        </li>
      </ul>
    </td>

    {props.allColumns.map((column, idx) => {
      if (column.kind === 'field') {
        return <td key={app + "-" + column.key + "-" + idx} title={column.key === 'last_message_text' ? getDisplayText(request.row, column.key) : undefined} className={`whitespace-wrap max-w-60 py-4 px-2 text-sm text-gray-900 ${column.key === 'unhandled_messages' && 'text-right'}`}>
          {column.key === 'support_case_status' && request.row.support_case_status && <SupportCaseBadge status={request.row.support_case_status as typeof caseStatuses[number]} />}
          {column.key === 'unhandled_messages' && <>{request.row.unhandled_count}</>}
          {(column.key === 'min' || column.key === 'max' || column.key === 'case_created') && <>{request.row[column.key] && new Date(request.row[column.key]!).toLocaleString(langForDate(context.lang))}</>}
          {column.key === 'tags' && <>{request.row.tags?.length ? request.row.tags.join(', ') : ''}</>}
          {column.key === 'last_message_text' && (
            <div className="overflow-hidden text-ellipsis [display:-webkit-box] [-webkit-box-orient:vertical] [-webkit-line-clamp:3]">
              {getDisplayText(request.row, column.key)}
            </div>
          )}
          {column.key === 'applicant_stage' ? <>{localizeStage(getStageFromRequest(request))}</> :
            <>{!['support_case_status',
              'unhandled_messages', 'min', 'max', 'case_created', 'tags', 'last_message_text'].includes(column.key)
              ? (maskSystemUser(request.row as any, column) || '').toLocaleString(langForDate(context.lang))
              : <></>}</>}
        </td> 
      } else {
        return <td key={app + "-" + column.key + "-" + idx} className="whitespace-wrap py-4 px-2 text-sm text-gray-900">
          {Object.values(request.applicantInfos).map(info => info[column.key]).filter(Boolean).join(', \n')}
        </td>
      }
    })}
  </>
}
